import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { topupApi } from '@/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn, formatRub } from '@/lib/utils';

interface TopupDialogProps {
  open: boolean;
  amountMinor: number; // сумма пополнения в копейках
  amountRub: number; // та же сумма в рублях для бэка
  onClose: () => void;
}

type Method = 'crypto' | 'sberpay' | 'sbp' | 'card' | 'yoomoney';

const METHODS: Array<{
  key: Method;
  label: string;
  icon: string;
  provider: 'cryptobot' | 'yookassa';
}> = [
  { key: 'sberpay', label: 'SberPay', icon: '/icons/sberpay.svg', provider: 'yookassa' },
  { key: 'sbp', label: 'СБП', icon: '/icons/sbp.svg', provider: 'yookassa' },
  { key: 'card', label: 'Банковская карта (РФ)', icon: '/icons/mir.png', provider: 'yookassa' },
  { key: 'yoomoney', label: 'ЮMoney', icon: '/icons/yoomoney.svg', provider: 'yookassa' },
  { key: 'crypto', label: 'Криптовалюта (CryptoBot)', icon: '/icons/crypto.svg', provider: 'cryptobot' },
];

export function TopupDialog({ open, amountMinor, amountRub, onClose }: TopupDialogProps) {
  const [picked, setPicked] = useState<Method | null>(null);

  const goMut = useMutation({
    mutationFn: async (m: Method) => {
      const cfg = METHODS.find((x) => x.key === m)!;
      if (cfg.provider === 'cryptobot') return topupApi.cryptobot(amountRub);
      return topupApi.yookassa(amountRub);
    },
    onSuccess: (r) => {
      window.open(r.url, '_blank', 'noopener,noreferrer');
      onClose();
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      const detail = err.response?.data?.message;
      toast.error(detail ? `Ошибка: ${detail}` : 'Не удалось создать платёж');
    },
  });

  function pick(m: Method) {
    setPicked(m);
    goMut.mutate(m);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Пополнение баланса</DialogTitle>
          <DialogDescription>
            Сумма: <span className="text-eol-pink font-bold">{formatRub(amountMinor)}</span>. Выберите способ оплаты.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2">
          {METHODS.map((m) => {
            const isLoading = goMut.isPending && picked === m.key;
            return (
              <button
                key={m.key}
                onClick={() => pick(m.key)}
                disabled={goMut.isPending}
                className={cn(
                  'group flex items-center gap-3 rounded-lg border border-white/10 bg-eol-black/40 px-4 py-3 transition text-left',
                  'hover:border-eol-pink/40 hover:bg-eol-black/60 disabled:opacity-60 disabled:cursor-wait',
                  isLoading && 'border-eol-pink/60 bg-eol-pink/5',
                )}
              >
                <div className="h-10 w-14 grid place-items-center rounded-md bg-white/95 shrink-0 overflow-hidden">
                  <img src={m.icon} alt="" className="max-h-7 max-w-12 object-contain" />
                </div>
                <span className="flex-1 text-eol-white font-semibold">{m.label}</span>
                {isLoading && (
                  <span className="text-xs text-eol-pink font-semibold">открываем…</span>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={goMut.isPending}>
            Отмена
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
